import React, { useEffect, useState, useRef } from "react";
import "../UserProfileEdit/UserBasicDetailsEdit.css";
import { Formik } from "formik";
import * as Yup from "yup";
import { getUserById, UpdateUser } from "../api/api";
import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";
import bgImage from "../../assets/images/brown-background.jpg";

const validationSchema = Yup.object().shape({
  name: Yup.string().required("Name is required"),
  profession: Yup.string().required("Profession is required"),
  email: Yup.string().email("Invalid email").required("Email is required"),
  phoneNumber: Yup.string()
    .required("Phone number is required")
    .min(10, "Number should have a minimum of 10 digits")
    .max(13, "Number should have a maximum of 13 digits")
    .matches(/^[0-9]+$/, "Phone number must only contain digits"),
  dob: Yup.object()
    .shape({
      day: Yup.number().min(1).max(31).required("Day is required"),
      month: Yup.number().min(1).max(12).required("Month is required"),
      year: Yup.number()
        .min(1900)
        .max(new Date().getFullYear())
        .required("Year is required"),
    })
    .test("valid-date", "Invalid day for the selected month", (value) => {
      if (!value) return false;
      const { day, month, year } = value;
      const isLeapYear =
        year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0);
      const daysInMonth = [
        31,
        isLeapYear ? 29 : 28,
        31,
        30,
        31,
        30,
        31,
        31,
        30,
        31,
        30,
        31,
      ];
      return day <= daysInMonth[month - 1];
    }),
  gender: Yup.string().required("Gender is required"),
});

const UserBasicDetailsEdit = () => {
  const [userdata, setUserdata] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchUser = async () => {
      const user = JSON.parse(localStorage.getItem("currentUser"));
      if (user?.id || user?._id) {
        const data = await getUserById({ user_id: user.id || user._id });
        setUserdata(data);
      }
    };
    fetchUser();
  }, []);

  const handleSubmit = async (values) => {
    setIsLoading(true);
    try {
      const { day, month, year } = values.dob;
      const dob = `${year.padStart(4, "0")}-${month.padStart(
        2,
        "0"
      )}-${day.padStart(2, "0")}`;
      const user = JSON.parse(localStorage.getItem("currentUser"));
      const payload = {
        user_id: user.id || user._id,
        name: values.name,
        email: values.email,
        phone: values.phoneNumber,
        dob,
        gender: values.gender,
        profession: values.profession,
        address: values.address,
      };
      const response = await UpdateUser(payload);
      // alert(response.message || "Updated");
      if (response.success) {
        await Swal.fire({
          icon: "success",
          title: "Profile Updated",
          text: response.message || "Your profile has been updated.",
        }).then(() => navigate("/Sidebar/EditProfile"));
      } else {
        await Swal.fire({
          icon: "error",
          title: "Error",
          text: response.message || "Update failed. Please try again.",
        });
      }
    } catch (err) {
      console.error(err);
      alert("Something went wrong");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div
      className="basicEditform-container"
      style={{
        backgroundImage: `url(${bgImage})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
      }}
    >
      <h2>Please Update Your Details</h2>
      <Formik
        enableReinitialize
        initialValues={{
          name: userdata?.data?.name || "",
          email: userdata?.data?.email || "",
          phoneNumber: userdata?.data?.phone || "",
          profession: userdata?.data?.profession || "",
          address: userdata?.data?.address || "",
          dob: (() => {
            if (userdata?.data?.dob) {
              const parts = userdata.data.dob.split(/[-/]/);
              return { year: parts[0], month: parts[1], day: parts[2] };
            }
            return { day: "", month: "", year: "" };
          })(),
          gender: userdata?.data?.gender || "",
        }}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({
          handleChange,
          handleBlur,
          handleSubmit,
          values,
          errors,
          touched,
          setFieldValue,
        }) => (
          // <form onSubmit={handleSubmit} className="basicEditform-grid">
          <form
            onSubmit={(e) => {
              e.preventDefault();

              const dobError =
                errors.dob?.day ||
                errors.dob?.month ||
                errors.dob?.year ||
                errors.dob;

              if (dobError) {
                let message = dobError;

                // Replace field names with readable labels
                if (typeof message === "string") {
                  message = message
                    .replace("dob.day", "Day")
                    .replace("dob.month", "Month")
                    .replace("dob.year", "Year");
                }

                Swal.fire({
                  icon: "error",
                  title: "Invalid Date of Birth",
                  text: message,
                });
                return;
              }

              handleSubmit();
            }}
            className="basicEditform-grid"
          >
            <div className="basicEditform-row">
              <div className="basicEditform-field">
                <label>Name</label>
                <input
                  type="text"
                  name="name"
                  value={values.name}
                  onChange={handleChange}
                  onBlur={handleBlur}
                />
                {touched.name && errors.name && (
                  <p className="basicEditerror">{errors.name}</p>
                )}
              </div>

              <div className="basicEditform-field">
                <label>Email</label>
                <input
                  type="email"
                  name="email"
                  value={values.email}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  disabled
                />
                {touched.email && errors.email && (
                  <p className="basicEditerror">{errors.email}</p>
                )}
              </div>

              <div className="basicEditform-field">
                <label>Phone Number</label>
                <input
                  type="text"
                  name="phoneNumber"
                  value={values.phoneNumber}
                  onChange={handleChange}
                  onBlur={handleBlur}
                />
                {touched.phoneNumber && errors.phoneNumber && (
                  <p className="basicEditerror">{errors.phoneNumber}</p>
                )}
              </div>
            </div>

            <div className="basicEditform-row">
              <div className="basicEditform-field">
                <label>Date of Birth - Day</label>
                <input
                  type="text"
                  value={values.dob.day}
                  maxLength={2}
                  onChange={(e) =>
                    setFieldValue("dob.day", e.target.value.replace(/\D/, ""))
                  }
                />
              </div>

              <div className="basicEditform-field">
                <label>Month</label>
                <input
                  type="text"
                  value={values.dob.month}
                  maxLength={2}
                  onChange={(e) =>
                    setFieldValue("dob.month", e.target.value.replace(/\D/, ""))
                  }
                />
              </div>

              <div className="basicEditform-field">
                <label>Year</label>
                <input
                  type="text"
                  value={values.dob.year}
                  maxLength={4}
                  onChange={(e) =>
                    setFieldValue("dob.year", e.target.value.replace(/\D/, ""))
                  }
                />
              </div>
            </div>
            {touched.dob && errors.dob && (
              <p className="basicEditerror">
                {errors.dob?.day || errors.dob?.month || errors.dob?.year}
              </p>
            )}
            <div className="basicEditform-row">
              <div className="basicEditform-field">
                <label>Profession</label>
                <input
                  type="text"
                  name="profession"
                  value={values.profession}
                  onChange={handleChange}
                  onBlur={handleBlur}
                />
                {touched.profession && errors.profession && (
                  <p className="basicEditerror">{errors.profession}</p>
                )}
              </div>

              <div className="basicEditform-field">
                <label>Address</label>
                <input
                  type="text"
                  name="address"
                  value={values.address}
                  onChange={handleChange}
                  onBlur={handleBlur}
                />
                {touched.address && errors.address && (
                  <p className="basicEditerror">{errors.address}</p>
                )}
              </div>

              {/* <div className="basicEditform-field">
                <label>Gender</label>
                <select
                  name="gender"
                  value={values.gender}
                  onChange={handleChange}
                  onBlur={handleBlur}
                >
                  <option value="">Select</option>
                  <option value="M">Man</option>
                  <option value="F">Woman</option>
                </select>
                {touched.gender && errors.gender && (
                  <p className="basicEditerror">{errors.gender}</p>
                )}
              </div> */}
            </div>

            <div className="basicEditform-actions">
              <button
                type="button"
                onClick={() => window.history.back()}
                className="basicEditback-btn"
              >
                Back
              </button>
              <button
                type="submit"
                className="basicEditsubmit-btn"
                disabled={isLoading}
              >
                {isLoading ? "Updating..." : "Update"}
              </button>
            </div>
          </form>
        )}
      </Formik>
    </div>
  );
};

export default UserBasicDetailsEdit;
